import React, { useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { usePrefetchGlobalData, useCustomers, useLanguages, useServiceTypes, useCommunicationTypes } from '../hooks/useGlobalData'
import { setCustomers } from '../redux/customerSlice'
import { setLanguage } from '../redux/languagesSlice'
import { setservicetypes } from '../redux/serviceSlice'
import { setCommunication } from '../redux/communicationSlice'
import { AppDispatch } from '../redux/store'
import { getAuth } from '../modules/auth/core/AuthHelpers'

interface GlobalDataProviderProps {
  children: React.ReactNode
}

/**
 * GlobalDataProvider - Initializes and caches global data on app load
 * 
 * This component:
 * 1. Fetches common dropdown data once when the app loads (only when authenticated)
 * 2. Syncs react-query cache with Redux store for backward compatibility
 * 3. Provides cached data across all components via react-query
 * 
 * Usage: Wrap your app with this provider after QueryClientProvider
 * 
 * Note: Only fetches data when user is authenticated to prevent infinite reload loop on logout
 */
export const GlobalDataProvider: React.FC<GlobalDataProviderProps> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>()
  const { prefetchAll } = usePrefetchGlobalData()
  
  // Check if user is authenticated - only fetch data when logged in
  const isAuthenticated = useMemo(() => {
    const auth = getAuth()
    return !!(auth && auth.token)
  }, [])

  // Fetch global data using react-query hooks - only when authenticated
  const { data: customersData } = useCustomers({ enabled: isAuthenticated })
  const { data: languagesData } = useLanguages({ enabled: isAuthenticated })
  const { data: serviceTypesData } = useServiceTypes({ enabled: isAuthenticated })
  const { data: communicationTypesData } = useCommunicationTypes({ enabled: isAuthenticated })

  // Prefetch all data on mount - only when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      prefetchAll()
    }
  }, [prefetchAll, isAuthenticated])

  // Sync react-query data with Redux store for backward compatibility
  useEffect(() => {
    if (customersData) {
      dispatch(setCustomers(customersData))
    }
  }, [customersData, dispatch])

  useEffect(() => {
    if (languagesData) {
      dispatch(setLanguage(languagesData))
    }
  }, [languagesData, dispatch])

  useEffect(() => {
    if (serviceTypesData) {
      dispatch(setservicetypes(serviceTypesData))
    }
  }, [serviceTypesData, dispatch])

  useEffect(() => {
    if (communicationTypesData) {
      dispatch(setCommunication(communicationTypesData))
    }
  }, [communicationTypesData, dispatch])

  return <>{children}</>
}

export default GlobalDataProvider
